import NiceModal from '@ebay/nice-modal-react';
import { ActionIcon, Group, Paper, Stack, Text } from '@mantine/core';
import { IconSettings, IconFileWord, IconEdit, IconCopy } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { useToggleKeywordsPage } from '../../api/toggleKeywordsPage';
import { useToggleProphetPage } from '../../api/togglePage';
import { useToggleProphetPageRefuses } from '../../api/togglePageRefuses';
import { prophetTypes } from '../../types';
import { extractPageName, extractPageIdentifier } from '../../utils';

import { CloneModal } from './CloneModal';
import { EditModal } from './EditModal';
import { EnableButton } from './EnableButton';

import { PLATFORM_URL_SEGMENT } from '@/features/pages';
import { Departments, Platforms } from '@/types';
import { COLORS } from '@/utils/colors';

type Props = {
  page: prophetTypes.Page;
  platform: Platforms;
  department: Departments;
};

export const Page = ({ page, platform, department }: Props) => {
  const navigate = useNavigate();
  // Meta pages live under `/prophet/meta`, so links must not fall back to the `instagram` segment.
  const urlPlatform = PLATFORM_URL_SEGMENT[platform];

  const togglePage = useToggleProphetPage();
  const togglePageKeyword = useToggleKeywordsPage();
  const togglePageRefuses = useToggleProphetPageRefuses();

  return (
    <Paper shadow="md" radius="md" p="md">
      <Group justify="space-between">
        <Stack gap="xs">
          <Text size="sm">{extractPageName(page)}</Text>
          {extractPageIdentifier(page) && (
            <Text size="xs" opacity={0.5}>
              {extractPageIdentifier(page)}
            </Text>
          )}
          <Group gap="xs">
            <ActionIcon
              variant="filled"
              onClick={() => NiceModal.show(EditModal, { page, platform, department })}
              radius="md"
            >
              <IconEdit color={COLORS.text} />
            </ActionIcon>
            <ActionIcon
              variant="filled"
              onClick={() => navigate(`/prophet/${urlPlatform}/${page._id}`)}
              radius="md"
              style={{ alignSelf: 'flex-start' }}
            >
              <IconSettings />
            </ActionIcon>
            {platform === 'instagram' && (
              <Group gap="xs">
                <ActionIcon
                  variant="filled"
                  onClick={() => navigate(`/prophet/keywords/${urlPlatform}/${page._id}`)}
                  radius="md"
                  style={{ alignSelf: 'flex-start' }}
                >
                  <IconFileWord />
                </ActionIcon>
              </Group>
            )}
            <ActionIcon
              variant="filled"
              onClick={() => NiceModal.show(CloneModal, { page, platform, department })}
              radius="md"
              style={{ alignSelf: 'flex-start' }}
            >
              <IconCopy color={COLORS.text} />
            </ActionIcon>
          </Group>
        </Stack>
        <Stack gap="xs">
          <EnableButton
            isEnabled={page.config.isEnabled}
            title="Запросы"
            onClick={() =>
              togglePage.mutate({ pageId: page._id, isEnabled: page.config.isEnabled })
            }
            isLoading={togglePage.isLoading}
          />
          <EnableButton
            isEnabled={!!page.config.isRefusesEnabled}
            title="Возражения"
            onClick={() =>
              togglePageRefuses.mutate({
                pageId: page._id,
                isEnabled: !!page.config.isRefusesEnabled,
              })
            }
            isLoading={togglePageRefuses.isLoading}
          />
          {platform === 'instagram' && (
            <EnableButton
              isEnabled={page.config.isKeywordsEnabled}
              title="Кодовые"
              onClick={() => {
                togglePageKeyword.mutate({
                  pageId: page._id,
                  isEnabled: page.config.isKeywordsEnabled,
                });
              }}
              isLoading={togglePageKeyword.isLoading}
            />
          )}
        </Stack>
      </Group>
    </Paper>
  );
};
